import { IAuthProvider } from "./IAuthProvider"
import { oktaAuth } from "./oktaAuth"
import { auth0Auth } from "./auth0Auth"
import { AccessToken } from "@okta/okta-auth-js"

/**
 * Gets the configured auth provider from environment variables.
 * Defaults to "Okta" if not specified.
 */
function getConfiguredProvider(): string {
	return import.meta.env.VITE_AUTH_PROVIDER?.toString() || "Okta"
}

/**
 * Wrapper class that adapts the Okta SDK to the IAuthProvider interface
 */
class OktaAuthProviderWrapper implements IAuthProvider {
	readonly providerName = "Okta"

	async isAuthenticated(): Promise<boolean> {
		try {
			return await oktaAuth.isAuthenticated()
		} catch (error) {
			console.error("Okta isAuthenticated error:", error)
			return false
		}
	}

	async getAccessToken(): Promise<string | undefined> {
		try {
			return await oktaAuth.getAccessToken()
		} catch (error) {
			console.error("Okta getAccessToken error:", error)
			return undefined
		}
	}

	async getIdToken(): Promise<string | undefined> {
		try {
			return await oktaAuth.getIdToken()
		} catch (error) {
			console.error("Okta getIdToken error:", error)
			return undefined
		}
	}

	isLoginRedirect(): boolean {
		return oktaAuth.isLoginRedirect()
	}

	async signInWithRedirect(): Promise<void> {
		await oktaAuth.signInWithRedirect()
	}

	async handleCallback(): Promise<{ accessToken?: string; idToken?: string }> {
		try {
			await oktaAuth.handleLoginRedirect()
			const accessToken = await oktaAuth.getAccessToken()
			const idToken = await oktaAuth.getIdToken()
			return { accessToken, idToken }
		} catch (error) {
			console.error("Okta handleCallback error:", error)
			throw error
		}
	}

	async signOut(): Promise<void> {
		this.clearTokens()
		try {
			// revokeAccessToken only — avoids post_logout_redirect_uri validation
			await oktaAuth.revokeAccessToken()
			await oktaAuth.revokeRefreshToken()
		} catch {
			// Ignore revocation errors — tokens are already cleared locally
		}
		window.location.href = "/login"
	}

	async start(): Promise<void> {
		await oktaAuth.start()
	}

	onTokenRenewed(callback: (key: string, token: string) => void): void {
		oktaAuth.tokenManager.on("renewed", (key, token) => {
			if (key === "accessToken") {
				const accessTokenData = token as AccessToken
				callback(key, accessTokenData.accessToken)
			}
		})
	}

	onTokenExpired(callback: (key: string) => void): void {
		oktaAuth.tokenManager.on("expired", (key) => {
			callback(key)
		})
	}

	onError(callback: (error: Error) => void): void {
		oktaAuth.tokenManager.on("error", (err) => {
			callback(err as Error)
		})
	}

	clearTokens(): void {
		oktaAuth.tokenManager.clear()
		sessionStorage.clear()
		localStorage.removeItem("token")
		localStorage.removeItem("PersonSessionData")
	}

	async renewToken(): Promise<string | undefined> {
		try {
			const token = await oktaAuth.tokenManager.renew("accessToken")
			if (token) {
				const accessToken = (token as AccessToken).accessToken
				localStorage.setItem("token", accessToken)
				return accessToken
			}
			return undefined
		} catch (error) {
			console.error("Okta renewToken error:", error)
			return undefined
		}
	}
}

// Create singleton instances
const oktaAuthWrapper = new OktaAuthProviderWrapper()

/**
 * Factory function that returns the appropriate auth provider based on configuration.
 *
 * Usage:
 * ```typescript
 * import { getAuthProvider } from './authProviderFactory'
 *
 * const authProvider = getAuthProvider()
 * await authProvider.signInWithRedirect()
 * ```
 */
export function getAuthProvider(): IAuthProvider {
	const provider = getConfiguredProvider()

	if (provider.toLowerCase() === "auth0") {
		return auth0Auth
	}

	// Default to Okta
	return oktaAuthWrapper
}

/**
 * Returns the name of the currently configured auth provider
 */
export function getAuthProviderName(): string {
	return getConfiguredProvider()
}

/**
 * Checks if Auth0 is the configured provider
 */
export function isAuth0Provider(): boolean {
	return getConfiguredProvider().toLowerCase() === "auth0"
}

/**
 * Checks if Okta is the configured provider
 */
export function isOktaProvider(): boolean {
	return getConfiguredProvider().toLowerCase() === "okta"
}

// Export the singleton auth provider for convenience
export const authProvider = getAuthProvider()
